import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { useInView } from 'react-intersection-observer'
import Thumbnail from 'components/Thumbnail'
import { Button, Typography } from 'components/common'
import {
  Comment as TComment,
  User,
  useCommentChangedSubscription,
  useDeleteThreadMutation,
  useReplyAddedSubscription
} from 'graphql/generated'
import { humanTime } from 'lib/datetime'
import { useEffect, useMemo, useState } from 'react'
import CommentInput from './CommentInput'
import InfiniteList from 'components/InfiniteList'
import useCommentReplies from './useCommentReplies'
import { twMerge } from 'tailwind-merge'
import DropdownMenu, {
  MenuItemProps
} from 'components/common/Dropdown/DropdownMenu'
import { useCollectiveContext } from 'context/CollectiveContext'
import { useCurrentUser } from 'hooks'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import { useForumContext } from './ForumContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faReply } from '@fortawesome/pro-solid-svg-icons'
import useConvertS3KeysToImageUrl from './useConvertS3KeysToImageUrls'
import Spinner from 'components/Spinner'
import QuillContent from 'components/QuillContent'
import { useAnalyticsContext } from 'context/AnalyticsContext'

export type TCommentType = Pick<
  TComment,
  'id' | 'body' | 'replyCount' | 'updatedAt' | 'createdAt' | 'attachments'
> & {
  user: Pick<User, 'publicUrl' | 'displayName' | 'thumb' | 'id'>
}
const Comment: React.FC<{
  parentId: string
  comment: TCommentType
  onDelete: Callback
  disableReplies?: boolean
  wrapperClassName?: string
  commentLevel?: number
}> = ({
  parentId,
  comment,
  onDelete,
  disableReplies,
  wrapperClassName,
  commentLevel = 1
}) => {
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const { collective } = useCollectiveContext()
  const viewer = useCurrentUser()
  const [showReplyInput, setShowReplyInput] = useState<boolean>()
  const { ref, inView } = useInView()
  const [showingReplies, setShowingReplies] = useState<boolean>(false)
  const isReply = commentLevel > 1

  const updateUrls = useConvertS3KeysToImageUrl(collective.discussionBoard.id)

  const [body, setBody] = useState<string>()
  useEffect(() => {
    async function parseBodyUrl() {
      setBody(await updateUrls(comment.body))
    }

    parseBodyUrl()
  }, [comment, updateUrls])

  const skipLoad = !inView || !!disableReplies || !showingReplies
  const { replies, loadMoreComments, loadingMore, refetch, loading } =
    useCommentReplies(comment.id, skipLoad)

  useCommentChangedSubscription({
    variables: { commentId: comment.id }
  })

  useReplyAddedSubscription({
    variables: { commentId: comment.id },
    onData: () => {
      refetch()
    }
  })

  const [deleteThread, { loading: isDeleting }] = useDeleteThreadMutation({
    onCompleted: () => {
      onDelete() //refetch parent
    }
  })

  const { selectedCategory } = useForumContext()

  const [editing, setEditing] = useState<boolean>(false)
  const confirm = useConfirmPromptModal()

  const isPrivileged = collective.membership?.privileged
  const isOwner = comment.user.id === viewer.id

  const dropdownOptions = useMemo(() => {
    const options: MenuItemProps[] = []

    if (isPrivileged || isOwner) {
      options.push({
        label: 'Edit',
        type: 'button',
        onClick: () => {
          setEditing(true)
          setShowReplyInput(false)
        }
      })
    }

    if (isPrivileged) {
      options.push({
        label: 'Delete Comment',
        type: 'button',
        loading: isDeleting,
        disabled: isDeleting,
        color: 'red',
        onClick: () => {
          confirm({
            title: 'Delete Comment',
            body: 'Are you sure you want to remove the comment?',
            onClick: async () =>
              deleteThread({
                variables: {
                  threadId: comment.id
                },
                onCompleted: () => {
                  if (analyticsLoaded) {
                    window.analytics?.trackAll(`Forum - Comment - Delete`, {
                      threadId: comment.id
                    })
                  }
                }
              }),
            colorScheme: 'red'
          })
        }
      })
    }
    return options
  }, [
    analyticsLoaded,
    comment.id,
    confirm,
    deleteThread,
    isDeleting,
    isOwner,
    isPrivileged
  ])

  const showReplyButton = !selectedCategory.archived && !disableReplies

  return (
    <div className={twMerge('relative p-5', wrapperClassName)} ref={ref}>
      <div className="group">
        <div className="w-full flex gap-2 justify-between">
          <div className="flex-1 flex items-center gap-1">
            <Thumbnail
              src={comment.user.thumb}
              className="h-7 w-7 rounded-full overflow-hidden"
            />

            <Typography
              color="gray"
              size="sm"
              className="text-blue"
              href={comment.user.publicUrl}
            >
              {comment.user.displayName}
            </Typography>

            <Typography color="gray" size="xs">
              {` \u00b7 ${humanTime(comment.createdAt, true)}`}
            </Typography>

            {comment.updatedAt !== comment.createdAt && (
              <Typography color="gray" size="xs">
                {` \u00b7 Edited ${humanTime(comment.updatedAt, true)}`}
              </Typography>
            )}
          </div>

          <div
            className={twMerge(
              'flex items-center gap-2',
              isReply && 'hidden group-hover:flex'
            )}
          >
            {showReplyButton && (
              <div className="cursor-pointer">
                <Button
                  size="sm"
                  color="textOnly"
                  onClick={() => {
                    setShowReplyInput(!showReplyInput)
                    setEditing(false)
                  }}
                  icon={faReply}
                  label="Reply"
                />
              </div>
            )}

            <DropdownMenu items={dropdownOptions} buttonClassName="h-7 w-5" />
          </div>
        </div>

        <div className="mt-2">
          {!editing ? (
            !body ? (
              <Spinner />
            ) : (
              <QuillContent content={body} />
            )
          ) : (
            <CommentInput
              placeHolder={`Enter ${isReply ? 'reply' : 'comment'} here`}
              saveButtonLabel={`Save ${isReply ? 'Reply' : 'Comment'}`}
              parentId={parentId}
              comment={comment}
              onSave={() => {
                setEditing(false)
                refetch()
              }}
              onCancel={() => {
                setEditing(false)
              }}
              type={isReply ? 'Reply' : 'Comment'}
            />
          )}
        </div>
      </div>

      {showReplyInput && (
        <CommentInput
          placeHolder="Enter reply here"
          saveButtonLabel="Reply"
          parentId={comment.id}
          onSave={() => {
            setShowReplyInput(false)
            refetch()
          }}
          onCancel={() => {
            setShowReplyInput(false)
          }}
          type="Reply"
        />
      )}

      {comment.replyCount > 0 && (
        <Typography
          size="sm"
          color="gray"
          className="group mt-4 cursor-pointer p-1 px-2 space-x-1 transition-all ease-in-out duration-200 border border-transparent hover:border-gray-200 rounded-md"
          onClick={() => setShowingReplies(!showingReplies)}
        >
          <FontAwesomeIcon size="sm" icon={faReply} />

          <span>{` ${comment.replyCount} Replies `}</span>

          <FontAwesomeIcon
            size="sm"
            icon={faChevronRight}
            className={twMerge(
              'transition-all duration-200 ease-in-out',
              showingReplies ? 'rotate-90' : ''
            )}
          />
        </Typography>
      )}

      {showingReplies && (
        <InfiniteList
          className="mt-4 pl-2 border-l-2 border-gray-200"
          items={replies}
          loadingMore={loadingMore}
          loadMore={loadMoreComments}
          renderItem={item => (
            <Comment
              wrapperClassName={'p-1 pl-2 pr-0'}
              onDelete={() => {
                refetch()
              }}
              parentId={comment.id}
              key={item.id}
              comment={item}
              disableReplies={commentLevel > 0}
              commentLevel={commentLevel + 1}
            />
          )}
          loading={loading}
          emptyText={<span />}
        />
      )}
    </div>
  )
}

export default Comment
